// Ações universais dos itens (0.52; padrão novo na 0.65), iguais em todas as abas liberadas:
// - arrastar o item para a DIREITA: excluir, sempre com a pergunta "Cancelar / Excluir"
//   (itens grandes, como a turma, o paciente ou a viagem inteira, pedem antes a senha ou a biometria);
// - arrastar para a ESQUERDA: abre os botões Arquivar e Duplicar, cada um com 1 toque;
// - menu ⋮ do item: Enviar (manda uma cópia para outra aba liberada) e Fixar no topo ⭐.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Animated, PanResponder, Platform, Pressable, Text, View } from 'react-native';
import { cancelarAviso, definirAbrirTarefa, pedirMeAvise, temAviso, useMeAvise, type AlvoAviso } from '../lib/meAvise';
import type { AbaId, Block, Box, Item, Link } from '../store/types';
import { MOD_IDS } from '../store/types';
import { inserirBox, novoBloco } from '../store/anotacoes';
import { salvarRefeicao, TODOS_DIAS } from '../store/bemestar';
import { novaTarefaCasa, salvarTarefaCasa } from '../store/casa';
import { horaTela, parseYmd, today, uid } from '../lib/date';
import { t } from '../lib/i18n';
import { paraOLado, toqueInterno, zerarToqueInterno } from '../lib/gestos';
import { segurandoItem, useAreaRolagem } from './Arrastar';
import { ABAS, C, aba, useCores } from './theme';
import { ActionSheet, type Action } from './ui';
import { useTaskSheet } from './TaskSheet';
import { DestinoEstudos } from './DestinoEstudos';
import { useConfirmarIdentidade } from './ConfirmarIdentidade';
import { getDoc } from '../store/store';
import { ABAS_SIGILO, confirmarItem, estaAguardando, marcarAguardando, paraPlanner, pedirDataConfirmar, useAguardando } from '../lib/envioEstado';

/** O que vai de uma aba para outra: título, itens de checklist, texto, data e hora. */
export type Pacote = {
  titulo: string; emoji?: string; itens?: string[]; texto?: string; data?: string | null; hora?: string | null;
  origem: AbaId;
  remover?: () => void;   // tirar o item da aba de origem depois de enviar (transferir)
  extras?: Action[];      // destinos dentro da própria aba (ex.: outra viagem, roteiro), aparecem primeiro (0.59)
  link?: Link;            // 0.65: o item continua ligado à origem (tocar no título na outra aba abre o item lá)
  soTitulo?: boolean;     // 0.65 (Diário): na outra aba vai SÓ o título, sem texto nem anotações
  onEnviado?: (destino: { aba: AbaId; tarefa?: string; caixa?: string }) => void;
};

/** Abas que recebem itens de outras abas. */
const RECEBEM: AbaId[] = ['planner', 'anotacoes', 'casa', 'estudos', 'bemestar', 'financas', 'documentos', ...MOD_IDS];

const itensDe = (p: Pacote): Item[] => (p.itens ?? []).map(x => x.trim()).filter(Boolean).map(text => ({ id: uid(), text, done: false }));
/** O pacote vira uma caixa de anotações (aparece em "Anotações recebidas" da aba de destino). */
export function caixaDoPacote(p: Pacote, destino: AbaId): Box {
  const blocks: Block[] = [];
  const its = itensDe(p);
  if (its.length) blocks.push({ ...novoBloco('check'), items: its });
  const txt = [p.texto?.trim(), p.data ? '📅 ' + p.data.split('-').reverse().join('/') + (p.hora ? ' ' + horaTela(p.hora) : '') : ''].filter(Boolean).join('\n');
  if (txt) blocks.push({ ...novoBloco('text'), text: txt });
  if (!blocks.length) blocks.push(novoBloco('check'));
  // só o título (Diário): uma checklist com o título; marcar o item conta como concluído na origem
  if (p.soTitulo) return { id: uid(), title: [p.emoji, p.titulo].filter(Boolean).join(' '), aba: destino, createdAt: new Date().toISOString(),
    blocks: [{ ...novoBloco('check'), items: [{ id: uid(), text: p.titulo, done: false }] }], ...(p.link ? { link: p.link } : {}) };
  return { id: uid(), title: [p.emoji, p.titulo].filter(Boolean).join(' '), aba: destino, blocks, createdAt: new Date().toISOString(), ...(p.link ? { link: p.link } : {}) };
}

type Ctx = { enviar: (p: Pacote) => void; confirmar: (motivo: string) => Promise<boolean> };
const AcoesCtx = createContext<Ctx>({ enviar: () => {}, confirmar: async () => true });
export const useAcoes = () => useContext(AcoesCtx);

export function AcoesProvider({ children }: { children: ReactNode }) {
  const { liberada, accent, corDe } = useCores();
  const sheet = useTaskSheet();
  const [menu, setMenu] = useState<{ titulo: string; a: Action[] } | null>(null);
  const [estudos, setEstudos] = useState<{ box: Box; p: Pacote } | null>(null);
  const ident = useConfirmarIdentidade(accent);
  // tocar no aviso "⏰ Me avise em…" de uma tarefa abre a ficha dela (lib/meAvise)
  const abrirFicha = sheet.open;
  useEffect(() => {
    definirAbrirTarefa(id => { const tk = getDoc('planner').tasks.find(x => x.id === id); if (tk) abrirFicha({ task: tk, date: today() }); });
    return () => definirAbrirTarefa(null);
  }, [abrirFicha]);

  /** Depois de enviar: o item fica aqui também ou sai daqui? */
  const enviado = (p: Pacote, onde: string) => {
    if (!p.remover) return Alert.alert(t('Enviado'), t('Foi para {destino}.', { destino: onde }));
    Alert.alert(t('Enviado'), t('Foi para {destino}. Quer tirar daqui?', { destino: onde }), [
      { text: t('Manter aqui também'), style: 'cancel' },
      { text: t('Tirar daqui'), style: 'destructive', onPress: p.remover },
    ]);
  };

  const enviar = (p0: Pacote) => {
    // só o título: nada de texto, itens ou anotações vai junto (Diário, 0.65)
    const p: Pacote = p0.soTitulo ? { ...p0, texto: undefined, itens: undefined } : p0;
    const caixaPara = (destino: AbaId) => { const b = caixaDoPacote(p, destino); inserirBox(b); p.onEnviado?.({ aba: destino, caixa: b.id }); return b; };
    const a: Action[] = [...(p.extras ?? [])];
    for (const x of ABAS.filter(x => RECEBEM.includes(x.id) && x.id !== p.origem && liberada(x.id))) {
      if (x.id === 'planner') a.push({ label: '📅 ' + t('My Planner (vira tarefa)'), onPress: () => sheet.open({
        date: p.data || undefined,
        // texto 10 (E4/E3): das abas de saúde vai só título e horário; item "Aguardando confirmação" vai como provisório
        prefill: paraPlanner(p.origem, p.link?.id, { title: p.titulo, ...(p.emoji ? { emoji: p.emoji, emojiManual: true } : {}), start: p.data || today(), time: p.hora ?? null,
          subtasks: itensDe(p).map(i => ({ id: i.id, text: i.text })), notes: ABAS_SIGILO.includes(p.origem) ? '' : p.texto ?? '', ...(p.link ? { link: p.link } : {}) }),
        onSaved: tk => { p.onEnviado?.({ aba: 'planner', tarefa: tk.id }); enviado(p, 'My Planner'); },
      }) });
      else if (x.id === 'bemestar' && p.soTitulo) a.push({ label: `${x.icon} ${x.nome}`, onPress: () => { caixaPara('bemestar'); enviado(p, t('{aba}, em "Anotações recebidas"', { aba: x.nome })); } });
      else if (x.id === 'casa' && p.soTitulo) a.push({ label: `${x.icon} ${x.nome}`, onPress: () => { caixaPara('casa'); enviado(p, t('{aba}, em "Anotações recebidas"', { aba: x.nome })); } });
      else if (x.id === 'bemestar') a.push({ label: '🥗 ' + t('Bem-Estar · Cronograma alimentar'), onPress: () => {
        const dia = p.data || today();
        salvarRefeicao({ id: uid(), nome: p.titulo, icon: p.emoji || '🍽️', hora: p.hora || '12:00', itens: itensDe(p),
          dias: p.data ? [parseYmd(dia).getDay()] : TODOS_DIAS });
        enviado(p, t('Bem-Estar · Cronograma alimentar'));
      } });
      else if (x.id === 'casa') a.push({ label: `${x.icon} ${x.nome} · ${t('tarefa da casa')}`, onPress: () => {
        salvarTarefaCasa({ ...novaTarefaCasa(null, p.titulo, p.emoji || '🧹'), subs: itensDe(p),
          obs: p.texto?.trim() ? [{ ...novoBloco('text'), text: p.texto.trim() }] : [novoBloco('check')] });
        enviado(p, x.nome);
      } });
      else if (x.id === 'estudos' && !p.soTitulo) a.push({ label: `${x.icon} ${x.nome} · ${t('página de estudo')}`, onPress: () => setEstudos({ box: caixaDoPacote(p, 'estudos'), p }) });
      else if (x.id === 'anotacoes') a.push({ label: `${x.icon} ${x.nome}`, onPress: () => { caixaPara('anotacoes'); enviado(p, x.nome); } });
      else a.push({ label: `${x.icon} ${x.nome}`, onPress: () => {
        caixaPara(x.id);
        enviado(p, t('{aba}, em "Anotações recebidas"', { aba: x.nome }));
      } });
    }
    if (!a.length) return Alert.alert(t('Nenhuma aba liberada para receber'), t('Libere outras abas em Ajustes › Compras.'));
    setMenu({ titulo: t('Enviar "{nome}" para…', { nome: p.titulo || t('este item') }), a });
  };

  const valor: Ctx = { enviar, confirmar: ident.pedir };
  return (
    <AcoesCtx.Provider value={valor}>
      {children}
      <ActionSheet title={menu?.titulo ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      <DestinoEstudos box={estudos?.box ?? null} cor={corDe('estudos')} onClose={() => setEstudos(null)}
        onEnviada={onde => { const p = estudos?.p; if (p) setTimeout(() => enviado(p, onde), 300); }} />
      {ident.tela}
    </AcoesCtx.Provider>
  );
}

const BOTAO = 76;

/** Ações do menu ⋮ de um item (para as telas que já têm o próprio menu): Enviar e Fixar no topo. */
export function acoesDoMenu(enviar: (p: Pacote) => void, o: { pacote?: Pacote | (() => Pacote); fixar?: { on: boolean; alternar: () => void }; lembrar?: AlvoAviso | null }): Action[] {
  const a: Action[] = [];
  // 0.67 (G2): "⏰ Me avise em…" (não cria tarefa no My Planner)
  if (o.lembrar && Platform.OS !== 'web') {
    const alvo = o.lembrar;
    a.push(temAviso(alvo) ? { label: '⏰ ' + t('Cancelar aviso'), onPress: () => { cancelarAviso(alvo); } } : { label: '⏰ ' + t('Me avise em…'), onPress: () => pedirMeAvise(alvo) });
  }
  if (o.pacote) a.push({ label: '📤 ' + t('Enviar para outra aba'), onPress: () => enviar(typeof o.pacote === 'function' ? o.pacote() : o.pacote!) });
  // texto 10 (E3): "⏳ Aguardando confirmação" nos itens com data (ex.: marquei dia 19 e espero o retorno)
  if (o.lembrar) {
    const alvo = o.lembrar;
    if (estaAguardando(alvo.aba, alvo.id)) {
      a.push({ label: '✅ ' + t('Confirmar'), onPress: () => confirmarItem(alvo.aba, alvo.id) });
      a.push({ label: '🔔 ' + t('Me lembre de confirmar'), onPress: () => { setTimeout(() => pedirDataConfirmar({ aba: alvo.aba, id: alvo.id }), 350); } });
    } else {
      let temData = false;
      try { const p = typeof o.pacote === 'function' ? o.pacote() : o.pacote; temData = !!p?.data; } catch { temData = false; }
      if (temData) a.push({ label: '⏳ ' + t('Aguardando confirmação'), onPress: () => {
        marcarAguardando({ aba: alvo.aba, id: alvo.id, titulo: alvo.titulo });
        setTimeout(() => pedirDataConfirmar({ aba: alvo.aba, id: alvo.id }), 350);   // oferece "Me lembre de confirmar" (fechar = sem lembrete)
      } });
    }
  }
  if (o.fixar) a.push({ label: o.fixar.on ? '⭐ ' + t('Desafixar do topo') : '⭐ ' + t('Fixar no topo'), onPress: o.fixar.alternar });
  return a;
}

/** Envolve qualquer item de lista com as ações universais. */
export function AcoesItem({ nome, onExcluir, onDuplicar, onArquivar, rotuloArquivar, pacote, fixar, menuExtra, menuFim, menuProprio, menuPos = 'topo', protegido, semConfirmar, aviso, raio = 14, lembrar, children }: {
  nome: string;
  onExcluir?: () => void;
  onDuplicar?: () => void;
  onArquivar?: () => void;  // 0.65: arrastar para a esquerda mostra Arquivar e Duplicar
  rotuloArquivar?: string;
  pacote?: Pacote | (() => Pacote);   // Enviar (no menu ⋮)
  fixar?: { on: boolean; alternar: () => void };   // Fixar no topo (no menu ⋮)
  menuExtra?: Action[];
  menuFim?: Action[];       // 0.69: opções no fim do ⋮ (ex.: Arquivar e Excluir, depois de Enviar e Fixar)
  menuProprio?: boolean;    // o item já tem o próprio ⋮ (a tela põe Enviar e Fixar nele)
  menuPos?: 'topo' | 'baixo';   // onde fica o ⋮ (canto de cima ou de baixo, à direita)
  protegido?: boolean;      // pede senha ou biometria antes de excluir (itens grandes, Finanças)
  semConfirmar?: boolean;   // onExcluir já pergunta sozinho
  aviso?: string;
  raio?: number;
  lembrar?: AlvoAviso | null;   // 0.67 (G2): "⏰ Me avise em…" no ⋮ e o ⏰ pequeno enquanto o aviso estiver marcado
  children: ReactNode;
}) {
  const marcado = useMeAvise(lembrar);
  const aguardando = useAguardando(lembrar?.aba, lembrar?.id);   // texto 10 (E3)
  const { enviar, confirmar } = useAcoes();
  const x = useRef(new Animated.Value(0)).current;
  const larg = useRef(320);
  const aberto = useRef(false);
  const [lado, setLado] = useState<0 | 1 | -1>(0);
  const [menu, setMenu] = useState<Action[] | null>(null);
  const botoes = (onDuplicar ? 1 : 0) + (onArquivar ? 1 : 0);
  const bandeja = botoes * BOTAO;
  const ref = useRef({ nome, onExcluir, onDuplicar, pacote, protegido, semConfirmar, aviso, bandeja, excluir: async () => {} });
  // texto 28 (A5): enquanto o cartão desliza para o lado, a tela não rola
  const area = useAreaRolagem();
  const areaRef = useRef(area); areaRef.current = area;
  const travar = (on: boolean) => areaRef.current?.setRolagem(!on);

  const fechar = () => { aberto.current = false; Animated.spring(x, { toValue: 0, useNativeDriver: false, bounciness: 4 }).start(() => setLado(0)); };
  const abrir = () => { aberto.current = true; setLado(-1); Animated.spring(x, { toValue: -ref.current.bandeja, useNativeDriver: false, bounciness: 4 }).start(); };

  const excluir = async () => {
    const r = ref.current;
    if (!r.onExcluir) return;
    if (r.semConfirmar && !r.protegido) return r.onExcluir();
    // B1: sempre pergunta; itens grandes pedem também a senha ou a biometria
    Alert.alert(t('Excluir {nome}?', { nome: r.nome || t('este item') }), [r.aviso, t('Fica 30 dias na lixeira.')].filter(Boolean).join('\n\n'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: async () => {
        if (r.protegido && !(await confirmar(t('Confirme para excluir')))) return;
        r.onExcluir!();
      } }]);
  };
  ref.current = { nome, onExcluir, onDuplicar, pacote, protegido, semConfirmar, aviso, bandeja, excluir };

  const pan = useMemo(() => {
    const horizontal = (_: unknown, g: { dx: number; dy: number }) => paraOLado(g)
      && (g.dx > 0 ? !!ref.current.onExcluir || aberto.current : ref.current.bandeja > 0);
    return PanResponder.create({
      // texto 26: o toque começou num item ou bloco de dentro (checklist, texto): o deslizar é dele, não do cartão
      onStartShouldSetPanResponderCapture: zerarToqueInterno,
      onMoveShouldSetPanResponder: (e, g) => !toqueInterno() && !segurandoItem() && horizontal(e, g),
      onMoveShouldSetPanResponderCapture: (e, g) => !toqueInterno() && !segurandoItem() && horizontal(e, g),
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => travar(true),
      onPanResponderMove: (_, g) => {
        const base = aberto.current ? -ref.current.bandeja : 0;
        let v = base + g.dx;
        if (!ref.current.onExcluir) v = Math.min(0, v);
        if (v < -ref.current.bandeja) v = -ref.current.bandeja + (v + ref.current.bandeja) / 3;   // resiste depois dos botões
        x.setValue(v); setLado(v > 0 ? 1 : v < 0 ? -1 : 0);
      },
      onPanResponderRelease: (_, g) => {
        travar(false);
        const base = aberto.current ? -ref.current.bandeja : 0;
        const v = base + g.dx;
        if (v > larg.current * 0.35 && ref.current.onExcluir) { fechar(); ref.current.excluir(); }
        else if (v < -Math.min(60, ref.current.bandeja / 2) && ref.current.bandeja) abrir();
        else fechar();
      },
      onPanResponderTerminate: () => { travar(false); if (aberto.current) abrir(); else fechar(); },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // montado só ao tocar no ⋮ (o "Aguardando confirmação" lê a data do item)
  const itensMenu = () => [...(menuExtra ?? []), ...acoesDoMenu(enviar, { pacote, fixar, lembrar }), ...(menuFim ?? [])];
  const temMenu = !menuProprio && (!!menuExtra?.length || !!menuFim?.length || !!pacote || !!fixar || (!!lembrar && Platform.OS !== 'web'));
  const opDir = x.interpolate({ inputRange: [0, 40], outputRange: [0, 1], extrapolate: 'clamp' });
  return (
    <View onLayout={e => { larg.current = e.nativeEvent.layout.width; }}>
      {lado === 1 && (
        <Animated.View pointerEvents="none" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, borderRadius: raio, backgroundColor: C.danger, opacity: opDir, justifyContent: 'center', paddingLeft: 18 }}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>🗑 {t('Excluir')}</Text>
        </Animated.View>
      )}
      {lado === -1 && (
        <View style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: bandeja, flexDirection: 'row', borderRadius: raio, overflow: 'hidden' }}>
          {!!onArquivar && (
            <Pressable accessibilityLabel={rotuloArquivar ?? t('Arquivar')} onPress={() => { fechar(); onArquivar(); }}
              style={{ width: BOTAO, backgroundColor: C.warn, alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Text style={{ color: '#fff', fontSize: 18 }}>📦</Text>
              <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }} numberOfLines={1}>{rotuloArquivar ?? t('Arquivar')}</Text>
            </Pressable>
          )}
          {!!onDuplicar && (
            <Pressable accessibilityLabel={t('Duplicar')} onPress={() => { fechar(); onDuplicar(); }}
              style={{ width: BOTAO, backgroundColor: '#6366F1', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Text style={{ color: '#fff', fontSize: 18 }}>⧉</Text>
              <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }} numberOfLines={1}>{t('Duplicar')}</Text>
            </Pressable>
          )}
        </View>
      )}
      <Animated.View {...pan.panHandlers} style={{ transform: [{ translateX: x }] }}>
        {children}
        {fixar?.on && <Text pointerEvents="none" style={{ position: 'absolute', left: 4, top: 2, fontSize: 11 }}>⭐</Text>}
        {!!marcado && <Text pointerEvents="none" style={{ position: 'absolute', left: fixar?.on ? 18 : 4, top: 2, fontSize: 11 }}>⏰</Text>}
        {!!aguardando && (
          <Pressable onPress={() => lembrar && Alert.alert('⏳ ' + t('Aguardando confirmação'), aguardando.lembrar ? t('Lembrete para confirmar: {data}', { data: new Date(aguardando.lembrar).toLocaleDateString() }) : '', [
            { text: t('Fechar'), style: 'cancel' }, { text: '✅ ' + t('Confirmar'), onPress: () => confirmarItem(lembrar.aba, lembrar.id) }])}
            hitSlop={6} style={{ alignSelf: 'flex-start', marginTop: 3, marginLeft: 6, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, backgroundColor: C.card2 }}>
            <Text style={{ color: C.sub, fontSize: 11 }}>⏳ {t('Aguardando confirmação')}</Text>
          </Pressable>
        )}
        {temMenu && (
          <Pressable onPress={() => setMenu(itensMenu())} hitSlop={10} accessibilityLabel={t('Mais opções')}
            style={[{ position: 'absolute', right: 2, width: 26, height: 26, alignItems: 'center', justifyContent: 'center' }, menuPos === 'baixo' ? { bottom: 2 } : { top: 2 }]}>
            <Text style={{ color: C.sub, fontSize: 17, fontWeight: '800' }}>⋮</Text>
          </Pressable>
        )}
        {/* durante o arraste, uma camada por cima evita que soltar o dedo conte como toque no item */}
        {lado !== 0 && <Pressable onPress={() => { if (aberto.current) fechar(); }} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0 }} />}
      </Animated.View>
      {temMenu && <ActionSheet title={nome || t('Item')} actions={menu} onClose={() => setMenu(null)} />}
    </View>
  );
}

/** Rótulo curto da aba para mensagens. */
export const nomeAba = (id: AbaId) => aba(id).nome;
